<template>
  <div>
    <p v-if="error" class="text-red-600">{{ error }}</p>
    <p v-else-if="!data" class="text-gray-500">Loading…</p>
    <div v-else class="grid grid-cols-12 gap-6">
      <div class="col-span-6 space-y-4">
        <div>
          <span class="block text-gray-500 mb-1">The right answer<template v-if="truth.truth_author"> · from {{ truth.truth_author }}</template></span>
          <p class="whitespace-pre-wrap text-gray-900 sensitive">{{ truth.answer || '—' }}</p>
        </div>
        <div v-if="!judgments.length && data.judge_verdict" class="rounded-md border border-gray-200 bg-white p-3">
          <span class="block text-gray-500 mb-1">Old holistic judge ({{ data.judge_score }} / 5). Rejudge the run to grade it per criterion.</span>
          <p class="text-gray-900 sensitive">{{ data.judge_verdict }}</p>
        </div>
        <div v-for="c in truth.criteria" :key="c.id" class="rounded-md border border-gray-200 bg-white p-3">
          <div class="flex items-start gap-2">
            <span :class="['mt-1 inline-block h-3 w-3 shrink-0 rounded-sm', DOT[(consensus[c.id] || {}).majority] || 'bg-gray-300']" />
            <div class="min-w-0 flex-1">
              <span class="text-[11px] font-medium uppercase tracking-wider text-gray-500">{{ c.kind === 'must_not' ? 'Must not' : c.id === 'answer' ? 'Right answer' : 'Must' }} · {{ c.id }}</span>
              <p class="text-gray-900 sensitive">{{ c.text }}</p>
              <p v-if="consensus[c.id]" class="text-gray-500">Judge: {{ VERDICT_LABEL[consensus[c.id].majority] }} · {{ agreeLine(consensus[c.id]) }}</p>
            </div>
          </div>
          <ul class="mt-2 space-y-2">
            <li v-for="(j, i) in perJudgment(c.id)" :key="i" class="border-l-2 border-gray-200 pl-3">
              <span class="text-gray-700">{{ i + 1 }}. {{ VERDICT_LABEL[j.verdict] || 'no verdict' }}<span v-if="j.counted !== j.verdict" class="text-amber-700"> → counted {{ VERDICT_LABEL[j.counted] }}: no quote found in the transcript</span></span>
              <p class="text-gray-600 sensitive">{{ j.reason }}</p>
              <p v-for="(e, k) in j.evidence" :key="k" class="text-gray-600">
                <button type="button" class="font-mono text-gray-500 hover:text-gray-900" @click="jump(e.entry)">[{{ e.entry }}]</button>
                <span :class="['sensitive', e.verified ? '' : 'line-through text-gray-400']">“{{ e.quote }}”</span>
              </p>
            </li>
          </ul>
          <div class="mt-2 flex items-center gap-2">
            <span class="text-gray-500">Your label</span>
            <button
              v-for="v in VERDICTS" :key="v" type="button"
              :class="['rounded border px-2 py-0.5', mine[c.id] === v ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-700 hover:border-gray-500']"
              @click="setLabel(c.id, v)"
            >{{ VERDICT_LABEL[v] }}</button>
          </div>
        </div>
        <div v-if="truth.criteria.length" class="flex items-center gap-3">
          <input v-model="note" :class="INPUT" placeholder="Note on your labels (optional)">
          <button type="button" :class="PRIMARY_BUTTON" :disabled="saving || !Object.keys(mine).length" @click="saveLabel">{{ saving ? 'Saving…' : 'Save labels' }}</button>
        </div>
        <p v-if="labelError" class="text-red-600">{{ labelError }}</p>
        <p v-for="l in data.labels || []" :key="l.by_id" class="text-gray-500">Labelled by {{ l.by }} {{ relative(l.at) }}{{ l.note ? `: ${l.note}` : '' }}</p>
        <div v-for="(j, i) in judgments" :key="`s${i}`" class="text-gray-600"><span class="text-gray-500">Judgment {{ i + 1 }} ({{ percent(j.score) }}):</span> <span class="sensitive">{{ j.summary }}</span></div>
        <div class="text-gray-500">
          <span class="block">Ended: <span class="sensitive">{{ data.ended || '—' }}</span></span>
          <span v-if="data.config" class="block">{{ configLine }}</span>
          <span v-if="(data.hats || []).length" class="block">Hats: {{ data.hats.join(', ') }}</span>
          <span v-if="(data.proposals || []).length" class="block">Writes proposed and declined: {{ data.proposals.map((p) => p.kind).join(', ') }}</span>
          <span v-for="(e, i) in data.errors || []" :key="`e${i}`" class="block text-red-700">{{ e }}</span>
        </div>
      </div>
      <div ref="transcript" class="col-span-6 space-y-2 max-h-[48rem] overflow-y-auto">
        <span class="block text-gray-500">Transcript, as the judge saw it</span>
        <p v-if="!entries.length" class="text-gray-500">No transcript stored. Rejudge the run to rebuild it from the session.</p>
        <div
          v-for="e in entries" :key="e.n" :ref="`entry${e.n}`"
          :class="['rounded-md px-3 py-2', e.role === 'user' ? 'bg-white border border-gray-200' : e.role === 'agent' ? 'bg-gray-100' : 'bg-white border border-dashed border-gray-200', cited.has(e.n) ? 'ring-2 ring-primary-400' : '']"
        >
          <span class="block text-[11px] font-medium uppercase tracking-wider text-gray-500">[{{ e.n }}] {{ roleLabel(e) }}</span>
          <p
            v-if="['user', 'agent', 'hats'].includes(e.role) || expanded.has(e.n)"
            :class="['whitespace-pre-wrap sensitive', e.role === 'call' || e.role === 'result' ? 'font-mono text-[12px] text-gray-700' : 'text-gray-900']"
          >{{ e.text }}</p>
          <button v-else type="button" class="text-gray-500 hover:text-gray-900" @click="expand(e.n)">Show {{ e.text.length }} characters</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API, INPUT, PRIMARY_BUTTON, errorOf, relative } from '../hermes';
import { VERDICTS, VERDICT_LABEL, percent } from './bench';

const DOT = { met: 'bg-green-600', partly: 'bg-amber-400', not_met: 'bg-red-600' };

export default {
  name: 'TestResultDetail',
  props: { resultId: { type: Number, required: true } },
  emits: ['labelled'],
  data() {
    return { data: null, error: '', mine: {}, note: '', saving: false, labelError: '', expanded: new Set(), DOT, VERDICTS, VERDICT_LABEL, INPUT, PRIMARY_BUTTON };
  },
  computed: {
    truth() {
      return (this.data && this.data.case_truth) || { criteria: [] };
    },
    judgments() {
      const all = (this.data && this.data.judgments) || [];
      const last = all.length ? all[all.length - 1] : {};
      return all.filter((j) => (j.truth_sha || '') === (last.truth_sha || '') && (j.judge_version || '') === (last.judge_version || ''));
    },
    consensus() {
      return Object.fromEntries(((this.data && this.data.criteria_consensus) || []).map((c) => [c.id, c]));
    },
    entries() {
      return (this.data && this.data.transcript) || [];
    },
    cited() {
      return new Set(this.judgments.flatMap((j) => (j.criteria || []).flatMap((c) => (c.evidence || []).filter((e) => e.verified).map((e) => e.entry))));
    },
    configLine() {
      const c = this.data.config || {};
      return [c.variant || 'deployed', c.model, c.thinking, `hats ${c.hats_enabled === '0' ? 'off' : 'on'}`, `prompt ${c.prompt_sha}`, `hats ${c.hats_sha}`, `tools ${c.tools_sha}`, `code ${c.code_sha}`].filter(Boolean).join(' · ');
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    percent, relative,
    roleLabel(e) {
      if (e.role === 'user') return 'User (judge)';
      if (e.role === 'agent') return 'Agent';
      if (e.role === 'hats') return 'Hats worn';
      return `${e.role === 'call' ? 'Tool call' : 'Tool result'} · ${e.name || ''}${e.error ? ' · error' : ''}`;
    },
    agreeLine(c) {
      return c.n > 1 ? `${Math.round(c.agreement * c.n)} of ${c.n} judgments agree` : 'one judgment';
    },
    perJudgment(id) {
      return this.judgments.map((j) => (j.criteria || []).find((c) => c.id === id)).filter(Boolean);
    },
    expand(n) {
      this.expanded = new Set([...this.expanded, n]);
    },
    jump(n) {
      this.expand(n);
      this.$nextTick(() => {
        const el = (this.$refs[`entry${n}`] || [])[0];
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    setLabel(id, v) {
      this.mine = { ...this.mine, [id]: v };
    },
    async load() {
      try {
        const { data } = await this.$axios.get(`${API}/console/bench/results/${this.resultId}/`);
        this.data = data;
        const own = data.my_label || null;
        this.mine = own ? { ...own.criteria } : {};
        this.note = own ? own.note || '' : '';
      } catch (e) {
        this.error = errorOf(e, 'The result could not be loaded.');
      }
    },
    async saveLabel() {
      this.saving = true;
      this.labelError = '';
      try {
        await this.$axios.post(`${API}/console/bench/results/${this.resultId}/label/`, { criteria: this.mine, note: this.note });
        await this.load();
        this.$emit('labelled');
      } catch (e) {
        this.labelError = errorOf(e, 'The labels were not saved.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
